import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Trophy, Award, MapPin, ChevronLeft, ChevronRight, ZoomIn, X } from "lucide-react";
import PageBanner from "./PageBanner";

const awardsData = [
  { id: 1, name: "2022 Outstanding Medium Student Branch", year: 2022, description: "Awarded for outstanding overall performance as a medium-sized student branch, recognizing excellence in activities, leadership, and impact.", image: "/asset/awards/Outstanding.webp", location: "Sousse, Tunisia", category: "Branch Performance" },
  { id: 2, name: "2023 Outstanding Medium Student Branch Award", year: 2023, description: "Recognizes a medium-sized student branch that demonstrated exceptional performance and consistency in its operations and initiatives.", image: "/asset/awards/outscan2023.webp", location: "Hammamet, Tunisia", category: "Branch Performance" },
  { id: 3, name: "2023 Regional Exemplary Student Branch Award", year: 2023, description: "Honors a student branch that stands out at the regional level for exemplary achievements, leadership, and impactful activities.", image: "/asset/awards/Regional.webp", location: "Tunisia", category: "Regional Excellence" },
  { id: 4, name: "Best Poster Award", year: 2024, description: "Awarded for delivering the best poster presentation, highlighting clarity, design quality, and effective communication.", image: "/asset/awards/posterscan.webp", location: "Tsyp12, Hammamet", category: "Presentation Excellence" },
  { id: 10, name: "Rising Star", year: 2024, description: "Awarded to Emna Awadni for her outstanding dedication, leadership, and impactful contributions to the community.", image: "/asset/awards/awadni.webp", location: "TSYP12, Hammamet", category: "Emerging Talent" },
  { id: 123, name: "Non-Technical Challenge – First Place", year: 2025, description: "Awarded First Place in the Non-Technical Challenge at WIE ACT 4.0 in Hammamet, Tunisia, in recognition of exceptional creativity, leadership, and outstanding performance.", image: "/asset/awards/nontechwiescan.jpg", location: "WIE ACT 4.0, Hammamet, Tunisia", category: "Leadership & Innovation" },
  { id: 5, name: "Outstanding WIE Student Affinity Group Award", year: 2025, description: "Recognizes an outstanding Women in Engineering (WIE) affinity group for excellence in activities, engagement, and impact.", image: "/asset/awards/wiescan.jpg", location: "TSYP13, Hammamet", category: "Women in Engineering" },
  { id: 6, name: "Outstanding Student Chapter Award (LARGE) — SMC SB Chapter", year: 2025, description: "Awarded to a large student chapter for outstanding performance, leadership, and contributions within its technical community.", image: "/asset/awards/smcscan.webp", location: "TSYP13, Hammamet", category: "Chapter Performance" },
  { id: 7, name: "3rd Place — IES Tunisia SB Chapter of the Year", year: 2025, description: "Recognizes a student chapter ranked third for its performance, achievements, and contributions over the year.", image: "/asset/awards/iesscan.webp", location: "TSYP13, Hammamet", category: "Ranking & Recognition" },
  { id: 8, name: "Rising Star", year: 2025, description: "Awarded to Mohamed Aziz Alimi for his leadership, innovation, and impactful role in advancing student activities and initiatives.", image: "/asset/awards/alimiscan.jpg", location: "TSYP13, Hammamet", category: "Emerging Talent" },
  { id: 9, name: "IEEE Regional Exemplary Student Branch Award", year: 2025, description: "Honors a student branch that demonstrates exemplary performance and distinction at the regional IEEE level.", image: "/asset/awards/rey8.webp", location: "Tunisia", category: "Regional Excellence" },
];

// Newest first, keeps original order inside a year
const sortedAwards = [...awardsData].sort((a, b) => b.year - a.year);
const years = [...new Set(sortedAwards.map((a) => a.year))];

export default function Awards() {
  const reduce = useReducedMotion();
  const [selectedYear, setSelectedYear] = useState(null);
  const [activeId, setActiveId] = useState(sortedAwards[0].id);
  const [zoomed, setZoomed] = useState(false);
  const closeRef = useRef(null);

  const list = useMemo(
    () => (selectedYear ? sortedAwards.filter((a) => a.year === selectedYear) : sortedAwards),
    [selectedYear]
  );

  // Keep the active award valid when the filter changes
  useEffect(() => {
    if (!list.some((a) => a.id === activeId)) setActiveId(list[0].id);
  }, [list, activeId]);

  const activeIndex = Math.max(0, list.findIndex((a) => a.id === activeId));
  const active = list[activeIndex];

  const go = useCallback(
    (dir) => setActiveId(list[(activeIndex + dir + list.length) % list.length].id),
    [list, activeIndex]
  );

  // Mobile thumbnail strip: keep the active thumb centered
  const stripRef = useRef(null);
  useEffect(() => {
    const strip = stripRef.current;
    const el = strip?.querySelector('[aria-current="true"]');
    if (!strip || !el) return;
    strip.scrollTo({
      left: el.offsetLeft - strip.clientWidth / 2 + el.clientWidth / 2,
      behavior: reduce ? "auto" : "smooth",
    });
  }, [activeId, list, reduce]);

  // Lightbox: Esc closes, arrows navigate, page scroll locked while open
  useEffect(() => {
    if (!zoomed) return;
    const onWinKey = (e) => {
      if (e.key === "Escape") setZoomed(false);
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onWinKey);
    closeRef.current?.focus();
    return () => {
      window.removeEventListener("keydown", onWinKey);
      document.body.style.overflow = prev;
    };
  }, [zoomed, go]);

  const onKey = (e) => {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") { e.preventDefault(); go(1); }
    if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); go(-1); }
  };

  return (
    <div className="min-h-screen bg-[#fafcff] relative">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ backgroundImage: `repeating-linear-gradient(-45deg, rgba(15,105,157,0.02) 0px, rgba(15,105,157,0.02) 1px, transparent 1px, transparent 18px)` }}
      />

      <PageBanner
        title="Awards"
        description="A record of outstanding contributions, excellence, and IEEE recognition earned by the IEEE ISIMS Student Branch."
        Icon={Trophy}
      />

      {/* ───────── Hall of recognition ───────── */}
      <main className="relative z-10 py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">

          {/* Section header: big count + year tabs */}
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-10 md:mb-14">
            <div className="flex items-end gap-5">
              <span
                className="text-[84px] sm:text-[110px] leading-[0.8] font-black tracking-tighter text-transparent select-none"
                style={{ WebkitTextStroke: "2px #006699" }}
                aria-hidden="true"
              >
                {String(sortedAwards.length).padStart(2, "0")}
              </span>
              <div className="pb-1">
                <h2 className="text-xl sm:text-2xl font-bold text-[#00334d] leading-tight">
                  Distinctions earned
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  Across {years.length} years of IEEE recognition. Pick one to open it.
                </p>
              </div>
            </div>

            <div role="tablist" aria-label="Filter awards by year" className="flex flex-wrap gap-px bg-[#006699]/15 p-px self-start lg:self-auto">
              {[null, ...years].map((y) => {
                const on = selectedYear === y;
                return (
                  <button
                    key={y ?? "all"}
                    role="tab"
                    aria-selected={on}
                    onClick={() => setSelectedYear(y)}
                    className={`px-4 sm:px-5 py-2.5 text-xs font-semibold tracking-wider transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#006699] focus-visible:ring-offset-2 ${
                      on ? "bg-[#006699] text-white" : "bg-white text-[#006699] hover:bg-[#006699]/5"
                    }`}
                  >
                    {y ?? "All"}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Spotlight + index */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12" onKeyDown={onKey}>

            {/* ── Spotlight (left) ── */}
            <section className="lg:col-span-7 lg:sticky lg:top-24 self-start" aria-live="polite">
              <div className="relative bg-[#00334d] overflow-hidden aspect-[4/3]">
                {/* blueprint grid */}
                <div
                  className="absolute inset-0 opacity-[0.12]"
                  style={{
                    backgroundImage:
                      "linear-gradient(#7cc4e8 1px, transparent 1px), linear-gradient(90deg, #7cc4e8 1px, transparent 1px)",
                    backgroundSize: "32px 32px",
                  }}
                />
                {/* giant outlined year */}
                <AnimatePresence mode="wait">
                  <motion.span
                    key={active.year}
                    initial={reduce ? false : { opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    className="absolute -bottom-4 -right-2 text-[140px] sm:text-[220px] leading-none font-black tracking-tighter text-transparent select-none pointer-events-none"
                    style={{ WebkitTextStroke: "1.5px rgba(124,196,232,0.35)" }}
                    aria-hidden="true"
                  >
                    {active.year}
                  </motion.span>
                </AnimatePresence>

                {/* corner brackets */}
                <span className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-[#7cc4e8] z-20" />
                <span className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-[#7cc4e8] z-20" />

                {/* zoom */}
                <button
                  onClick={() => setZoomed(true)}
                  aria-label="Open certificate in large view"
                  className="absolute top-3 right-3 z-30 w-10 h-10 bg-[#00334d]/80 hover:bg-[#006699] text-white border border-white/25 flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7cc4e8]"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>

                {/* mobile prev / next on the image itself */}
                <button onClick={() => go(-1)} aria-label="Previous award" className="lg:hidden absolute left-2 top-1/2 -translate-y-1/2 z-30 w-9 h-9 bg-[#00334d]/80 text-white border border-white/20 flex items-center justify-center active:bg-[#006699]">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button onClick={() => go(1)} aria-label="Next award" className="lg:hidden absolute right-2 top-1/2 -translate-y-1/2 z-30 w-9 h-9 bg-[#00334d]/80 text-white border border-white/20 flex items-center justify-center active:bg-[#006699]">
                  <ChevronRight className="w-4 h-4" />
                </button>

                {/* certificate (swipe left / right on touch) */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={active.id}
                    initial={reduce ? false : { opacity: 0, scale: 0.96, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                    drag="x"
                    dragConstraints={{ left: 0, right: 0 }}
                    dragElastic={0.25}
                    onDragEnd={(_, info) => {
                      if (info.offset.x < -60) go(1);
                      else if (info.offset.x > 60) go(-1);
                    }}
                    style={{ touchAction: "pan-y" }}
                    className="absolute inset-0 flex items-center justify-center px-12 py-8 sm:p-12 z-10"
                  >
                    <img
                      src={active.image}
                      alt={active.name}
                      className="max-w-full max-h-full object-contain shadow-[0_24px_60px_rgba(0,0,0,0.45)] ring-1 ring-white/20"
                    />
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Mobile quick-pick strip: sits right under the image, no long scroll */}
              <div className="lg:hidden bg-white border border-[#006699]/10 border-t-0">
                <div
                  ref={stripRef}
                  className="relative flex gap-2 overflow-x-auto snap-x snap-mandatory px-4 py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                  aria-label="All awards"
                >
                  {list.map((award) => {
                    const on = award.id === active.id;
                    return (
                      <button
                        key={award.id}
                        onClick={() => setActiveId(award.id)}
                        aria-current={on}
                        aria-label={`${award.year} ${award.name}`}
                        className={`snap-center flex-shrink-0 w-[72px] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#006699] transition-opacity ${on ? "opacity-100" : "opacity-50"}`}
                      >
                        <img
                          src={award.image}
                          alt=""
                          loading="lazy"
                          className={`w-full h-14 object-cover ${on ? "ring-2 ring-[#006699]" : ""}`}
                        />
                        <span className={`block text-[11px] mt-1 font-bold tabular-nums ${on ? "text-[#006699]" : "text-[#00334d]/60"}`}>
                          {award.year}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="px-4 pb-3 -mt-1 text-[11px] text-slate-400">Swipe the certificate or tap a thumbnail</p>
              </div>

              {/* Caption plate */}
              <div className="bg-white border border-[#006699]/10 border-t-0 p-6 sm:p-8">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={active.id}
                    initial={reduce ? false : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <div className="flex items-center gap-2 mb-3 text-[#006699]">
                      <Award className="w-4 h-4" strokeWidth={2} />
                      <span className="text-xs font-semibold">{active.category}</span>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-[#00334d] leading-snug">{active.name}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed mt-3 max-w-[62ch]">{active.description}</p>
                    <div className="flex items-center gap-1.5 mt-5 text-[#00334d]/60">
                      <MapPin className="w-3.5 h-3.5" strokeWidth={2} />
                      <span className="text-xs font-medium">{active.location}</span>
                    </div>
                  </motion.div>
                </AnimatePresence>

                <div className="flex items-center justify-between mt-6 pt-5 border-t border-[#006699]/10">
                  <span className="text-xs font-semibold text-[#00334d]/60 tabular-nums">
                    {activeIndex + 1} of {list.length}
                  </span>
                  <div className="flex gap-px bg-[#006699]/15 p-px">
                    <button onClick={() => go(-1)} aria-label="Previous award" className="w-10 h-10 bg-white text-[#006699] flex items-center justify-center hover:bg-[#006699] hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#006699]">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button onClick={() => go(1)} aria-label="Next award" className="w-10 h-10 bg-white text-[#006699] flex items-center justify-center hover:bg-[#006699] hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#006699]">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* ── Index (right) ── */}
            <nav className="hidden lg:block lg:col-span-5" aria-label="All awards">
              <ul className="border-t border-[#006699]/15">
                {list.map((award, i) => {
                  const on = award.id === active.id;
                  const newYear = i === 0 || list[i - 1].year !== award.year;
                  return (
                    <React.Fragment key={award.id}>
                      {newYear && (
                        <li className="flex items-center gap-3 pt-6 pb-2 first:pt-4" aria-hidden="true">
                          <span className="text-2xl font-black text-[#006699] tracking-tight">{award.year}</span>
                          <span className="flex-1 h-px bg-[#006699]/15" />
                          <span className="text-xs font-semibold text-[#00334d]/40">
                            {list.filter((a) => a.year === award.year).length}
                          </span>
                        </li>
                      )}
                      <li>
                        <button
                          onClick={() => setActiveId(award.id)}
                          aria-current={on}
                          className={`group relative w-full text-left flex items-center gap-4 py-3.5 pl-5 pr-3 border-b border-[#006699]/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#006699] ${
                            on ? "bg-[#006699]/[0.06]" : "hover:bg-[#006699]/[0.03]"
                          }`}
                        >
                          <span className={`absolute left-0 top-0 bottom-0 w-[3px] bg-[#006699] origin-top transition-transform duration-300 ${on ? "scale-y-100" : "scale-y-0 group-hover:scale-y-50"}`} />
                          <img
                            src={award.image}
                            alt=""
                            loading="lazy"
                            className={`w-14 h-11 object-cover flex-shrink-0 transition-all duration-300 ${on ? "opacity-100 ring-1 ring-[#006699]" : "opacity-60 grayscale group-hover:opacity-100 group-hover:grayscale-0"}`}
                          />
                          <span className="flex-1 min-w-0">
                            <span className={`block text-sm leading-snug transition-colors ${on ? "font-bold text-[#00334d]" : "font-semibold text-[#00334d]/80"}`}>
                              {award.name}
                            </span>
                            <span className="block text-xs text-slate-400 mt-0.5 truncate">{award.category}</span>
                          </span>
                          <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-all duration-300 ${on ? "text-[#006699] translate-x-0 opacity-100" : "text-[#006699]/40 -translate-x-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-0"}`} />
                        </button>
                      </li>
                    </React.Fragment>
                  );
                })}
              </ul>
            </nav>
          </div>
        </div>
      </main>

      {/* ───────── Lightbox ───────── */}
      <AnimatePresence>
        {zoomed && (
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={active.name}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-gray-900/70 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
            onClick={() => setZoomed(false)}
          >
            <button
              ref={closeRef}
              onClick={() => setZoomed(false)}
              aria-label="Close"
              className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 w-11 h-11 bg-white/10 hover:bg-white/25 text-white border border-white/30 flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <X className="w-5 h-5" />
            </button>

            <button
              onClick={(e) => { e.stopPropagation(); go(-1); }}
              aria-label="Previous award"
              className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 bg-white/10 hover:bg-white/25 text-white border border-white/30 flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); go(1); }}
              aria-label="Next award"
              className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 bg-white/10 hover:bg-white/25 text-white border border-white/30 flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            <div className="flex flex-col items-center max-w-5xl w-full px-10 sm:px-16" onClick={(e) => e.stopPropagation()}>
              <AnimatePresence mode="wait">
                <motion.img
                  key={active.id}
                  src={active.image}
                  alt={active.name}
                  initial={reduce ? false : { opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.25}
                  onDragEnd={(_, info) => {
                    if (info.offset.x < -60) go(1);
                    else if (info.offset.x > 60) go(-1);
                  }}
                  style={{ touchAction: "pan-y" }}
                  className="max-w-full max-h-[75vh] object-contain bg-white shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
                />
              </AnimatePresence>
              <p className="mt-4 text-sm font-semibold text-white text-center leading-snug">{active.name}</p>
              <p className="mt-1 text-xs text-white/70 tabular-nums">
                {active.year} · {activeIndex + 1} of {list.length}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}